/**
 * Stellar DEX Dashboard Component
 * 
 * Displays Stellar order book, market maker status, and arbitrage opportunities.
 */

import React, { useState, useEffect } from 'react';
import {
  StellarMarketMaker,
  type StellarMarketMakerState,
  AURG_XLM_STELLAR_CONFIG
} from '../services/StellarMarketMaker';
import {
  StellarArbitrageMonitor,
  type StellarOrderBookSnapshot,
  type StellarArbitrageOpportunity,
  type CrossChainArbitrageOpportunity
} from '../services/StellarArbitrageMonitor';
import { grams } from '../services/types/Weight';

const ISSUER_PUBLIC_KEY = 'GDFPCZY5F4QLZQMB5CRR3LF644AP6R76QFY2CSFCZSCELEW5Z532SU7A';
const MARKET_MAKER_PUBLIC_KEY = 'GB73QRFVUQS3TJSRQV2BYNKKVWFSNRR2PZREM6MPIHPV7R4Q7BLTKTQB';

export const StellarDEXDashboard: React.FC = () => {
  const [orderBook, setOrderBook] = useState<StellarOrderBookSnapshot | null>(null);
  const [marketMakerState, setMarketMakerState] = useState<StellarMarketMakerState | null>(null);
  const [arbitrageOpp, setArbitrageOpp] = useState<StellarArbitrageOpportunity | null>(null);
  const [crossChainOpp, setCrossChainOpp] = useState<CrossChainArbitrageOpportunity | null>(null);
  const [spotPrice, setSpotPrice] = useState<number>(95.12);
  const [xrplPrice, setXrplPrice] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const marketMaker = React.useMemo(() => {
    return new StellarMarketMaker({
      ...AURG_XLM_STELLAR_CONFIG,
      marketMakerPublicKey: MARKET_MAKER_PUBLIC_KEY,
      issuerPublicKey: ISSUER_PUBLIC_KEY
    });
  }, []);
  
  const arbitrageMonitor = React.useMemo(() => {
    return new StellarArbitrageMonitor('AURG', ISSUER_PUBLIC_KEY, 'XLM');
  }, []);
  
  const loadData = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const [book, state] = await Promise.all([
        arbitrageMonitor.getOrderBook(),
        marketMaker.getState()
      ]);
      
      setOrderBook(book);
      setMarketMakerState(state);
      
      // Check arbitrage
      const opp = await arbitrageMonitor.checkArbitrage(spotPrice, grams(1.0));
      setArbitrageOpp(opp);
      
      // Check cross-chain if XRPL price available
      if (xrplPrice !== null) {
        const crossOpp = await arbitrageMonitor.checkCrossChainArbitrage(xrplPrice);
        setCrossChainOpp(crossOpp);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setIsLoading(false);
    }
  };
  
  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 15000);
    return () => clearInterval(interval);
  }, [spotPrice, xrplPrice]);
  
  return (
    <div style={{ padding: '20px', fontFamily: 'monospace' }}>
      <h1>🌟 Stellar DEX Dashboard</h1>
      <p style={{ color: '#666', marginBottom: '20px' }}>
        Real-time Stellar order book, market maker, and arbitrage monitoring for AURG/XLM
      </p>
      
      {error && (
        <div style={{ background: '#fee', padding: '10px', marginBottom: '20px', borderRadius: '4px' }}>
          <strong>Error:</strong> {error}
        </div>
      )}
      
      {/* Price Inputs */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
        <div>
          <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
            Gold Spot (USD/g):
          </label>
          <input
            type="number"
            value={spotPrice}
            onChange={(e) => setSpotPrice(parseFloat(e.target.value))}
            step={0.01}
            style={{ width: '100%', padding: '8px', fontSize: '16px' }}
          />
        </div>
        <div>
          <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
            XRPL Price (USD, optional):
          </label>
          <input
            type="number"
            value={xrplPrice ?? ''}
            onChange={(e) => setXrplPrice(e.target.value ? parseFloat(e.target.value) : null)}
            step={0.01}
            placeholder="Enter XRPL price..."
            style={{ width: '100%', padding: '8px', fontSize: '16px' }}
          />
        </div>
      </div>
      
      {/* Market Maker State */}
      {marketMakerState && (
        <div style={{ background: '#f9f9f9', padding: '15px', marginBottom: '20px', borderRadius: '8px' }}>
          <h2 style={{ marginTop: 0 }}>📊 Market Maker Status</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '15px' }}>
            <div>
              <div style={{ color: '#666', fontSize: '12px' }}>AURG Balance</div>
              <div style={{ fontSize: '18px', fontWeight: 'bold' }}>
                {parseFloat(marketMakerState.assetBalance).toFixed(4)}
              </div>
            </div>
            <div>
              <div style={{ color: '#666', fontSize: '12px' }}>XLM Balance</div>
              <div style={{ fontSize: '18px', fontWeight: 'bold' }}>
                {parseFloat(marketMakerState.baseBalance).toFixed(4)}
              </div>
            </div>
            <div>
              <div style={{ color: '#666', fontSize: '12px' }}>Mid Price</div>
              <div style={{ fontSize: '18px', fontWeight: 'bold' }}>
                {marketMakerState.midPrice > 0 ? marketMakerState.midPrice.toFixed(4) : 'N/A'}
              </div>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '15px', marginTop: '15px' }}>
            <div>
              <div style={{ color: '#666', fontSize: '12px' }}>Open Bids</div>
              <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#28a745' }}>
                {marketMakerState.openBids}
              </div>
            </div>
            <div>
              <div style={{ color: '#666', fontSize: '12px' }}>Bid Volume</div>
              <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#28a745' }}>
                {marketMakerState.totalBidVolume}
              </div>
            </div>
            <div>
              <div style={{ color: '#666', fontSize: '12px' }}>Open Asks</div>
              <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#dc3545' }}>
                {marketMakerState.openAsks}
              </div>
            </div>
            <div>
              <div style={{ color: '#666', fontSize: '12px' }}>Ask Volume</div>
              <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#dc3545' }}>
                {marketMakerState.totalAskVolume}
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Order Book */}
      {orderBook && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
          <div style={{ background: '#f9f9f9', padding: '15px', borderRadius: '8px' }}>
            <h3 style={{ marginTop: 0, color: '#28a745' }}>🟢 Bids (Buy Orders)</h3>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left', padding: '5px', borderBottom: '1px solid #ddd' }}>Price</th>
                  <th style={{ textAlign: 'right', padding: '5px', borderBottom: '1px solid #ddd' }}>Amount</th>
                </tr>
              </thead>
              <tbody>
                {orderBook.bids.slice(0, 10).map((bid, i) => (
                  <tr key={i}>
                    <td style={{ padding: '5px', color: '#28a745' }}>
                      {bid.price.toFixed(4)}
                    </td>
                    <td style={{ padding: '5px', textAlign: 'right' }}>
                      {bid.amount.toFixed(4)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          <div style={{ background: '#f9f9f9', padding: '15px', borderRadius: '8px' }}>
            <h3 style={{ marginTop: 0, color: '#dc3545' }}>🔴 Asks (Sell Orders)</h3>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left', padding: '5px', borderBottom: '1px solid #ddd' }}>Price</th>
                  <th style={{ textAlign: 'right', padding: '5px', borderBottom: '1px solid #ddd' }}>Amount</th>
                </tr>
              </thead>
              <tbody>
                {orderBook.asks.slice(0, 10).map((ask, i) => (
                  <tr key={i}>
                    <td style={{ padding: '5px', color: '#dc3545' }}>
                      {ask.price.toFixed(4)}
                    </td>
                    <td style={{ padding: '5px', textAlign: 'right' }}>
                      {ask.amount.toFixed(4)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      
      {/* Arbitrage Opportunities */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
        <div style={{ background: '#fff3cd', padding: '15px', borderRadius: '8px', border: '2px solid #ffc107' }}>
          <h3 style={{ marginTop: 0 }}>⚡ Single-Chain Arbitrage</h3>
          {arbitrageOpp ? (
            <div>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#856404', marginBottom: '10px' }}>
                {arbitrageOpp.type === 'mint-sell' ? '📤 MINT & SELL' : '📥 BUY & BURN'}
              </div>
              <div style={{ marginBottom: '8px' }}>
                <strong>DEX Price:</strong> ${arbitrageOpp.dexPrice.toFixed(4)}
              </div>
              <div style={{ marginBottom: '8px' }}>
                <strong>Spot Price:</strong> ${arbitrageOpp.spotPrice.toFixed(4)}
              </div>
              <div style={{ marginBottom: '8px' }}>
                <strong>Profit:</strong> {arbitrageOpp.profitPct.toFixed(2)}% ({arbitrageOpp.profitBps} bps)
              </div>
              <div style={{ marginBottom: '8px' }}>
                <strong>Size:</strong> {arbitrageOpp.size.toFixed(2)} AURG
              </div>
              <div>
                <strong>Gross Profit:</strong> ${arbitrageOpp.grossProfit.toFixed(2)}
              </div>
            </div>
          ) : (
            <div style={{ color: '#666' }}>No opportunities detected</div>
          )}
        </div>
        
        <div style={{ background: '#d1ecf1', padding: '15px', borderRadius: '8px', border: '2px solid #17a2b8' }}>
          <h3 style={{ marginTop: 0 }}>🌐 Cross-Chain Arbitrage</h3>
          {crossChainOpp ? (
            <div>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#0c5460', marginBottom: '10px' }}>
                {crossChainOpp.type === 'stellar-to-xrpl' ? '🌟→💎 STELLAR→XRPL' : '💎→🌟 XRPL→STELLAR'}
              </div>
              <div style={{ marginBottom: '8px' }}>
                <strong>Stellar Price:</strong> ${crossChainOpp.stellarPrice.toFixed(4)}
              </div>
              <div style={{ marginBottom: '8px' }}>
                <strong>XRPL Price:</strong> ${crossChainOpp.xrplPrice.toFixed(4)}
              </div>
              <div style={{ marginBottom: '8px' }}>
                <strong>Profit:</strong> {crossChainOpp.profitPct.toFixed(2)}% ({crossChainOpp.profitBps} bps)
              </div>
              <div style={{ marginBottom: '8px' }}>
                <strong>Size:</strong> {crossChainOpp.size.toFixed(2)} AURG
              </div>
              <div>
                <strong>Gross Profit:</strong> ${crossChainOpp.grossProfit.toFixed(2)}
              </div>
            </div>
          ) : (
            <div style={{ color: '#666' }}>
              {xrplPrice === null ? 'Enter XRPL price to enable' : 'No opportunities detected'}
            </div>
          )}
        </div>
      </div>
      
      {/* Refresh Button */}
      <div style={{ marginTop: '20px', textAlign: 'center' }}>
        <button
          onClick={loadData}
          disabled={isLoading}
          style={{
            padding: '12px 24px',
            fontSize: '16px',
            background: isLoading ? '#ccc' : '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: isLoading ? 'not-allowed' : 'pointer'
          }}
        >
          {isLoading ? 'Loading...' : '🔄 Refresh Data'}
        </button>
      </div>
      
      {/* Network Info */}
      <div style={{ marginTop: '20px', padding: '10px', background: '#e7f3ff', borderRadius: '4px', fontSize: '12px' }}>
        <strong>Network:</strong> Stellar Mainnet<br />
        <strong>Issuer:</strong> {ISSUER_PUBLIC_KEY}<br />
        <strong>Market Maker:</strong> {MARKET_MAKER_PUBLIC_KEY}<br />
        <strong>Asset:</strong> AURG/XLM
      </div>
    </div>
  );
};
